import { useContext, useEffect, useState } from 'react';
import {
  Button, Grid, TextField, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell, Box, Tooltip,
  IconButton, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Breadcrumbs,
  Link,
  Typography,
} from '@mui/material';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { SearchSm } from '../../../../assets';
import { UserContext } from '../../../../components/shared/useUser';
import { client, DeliveryOrderItemModel } from '../../../../app/services/api/orderManagementClient';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import PageTitle from '../../../../components/shared/PageTitle';
import PageArea from '../../../../components/shared/PageArea';
import BackButton from '../../../../components/shared/actions/BackButton';
import { useTranslation } from 'react-i18next';
import LeavePageModal from '../../../../components/shared/dialogs/LeavePageModal';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import TextWithOverflowTooltip from '../../../../components/shared/TextWithOverflowTooltip';
import OrderStatusChip from '../../../../components/shared/orders/OrderStatusChip';
import { Note, WarningAmber, Clear } from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { UOM_OPTIONS } from '../helpers/DeliveryHelpers';
import CustomPagination from '../../../../components/forms/Pagination';

const DeliveryDetailsOfd = () => {
  dayjs.extend(duration);
  const navigate = useNavigate();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { t } = useTranslation('orderHistory');
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [deliveryItems, setDeliveryItems] = useState<DeliveryOrderItemModel[]>([]);
  const [filteredItems, setFilteredItems] = useState<DeliveryOrderItemModel[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [itemQuantities, setItemQuantities] = useState<{ [key: number]: number }>({});
  const [itemUoms, setItemUoms] = useState<{ [key: number]: string }>({});
  const [itemNotes, setItemNotes] = useState<{ [key: number]: string }>({});
  const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
  const [receiveEnabled, setReceiveEnabled] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalItemId, setNotesModalItemId] = useState<number | null>(null);
  const [notesModalText, setNotesModalText] = useState<string>('');

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [discrepanciesExist, setDiscrepanciesExist] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'warning' | 'info' | 'success' }>({ open: false, message: '', severity: 'info' });

  const blocker = useBlocker(() => modifiedItems.size > 0);

  const orderIsReceived = deliveryItems[0]?.OrderReceived;
  const creditRequestExists = deliveryItems.some(item => item.CreditRequestItemID !== undefined && item.CreditRequestItemID !== null);
  const userIsReviewer = hasPermissionTo(['CreditRequestsReview']);
  const withinClaimPeriod = deliveryItems.some(item => item.HasPassedClaimPeriod === false);

  const fetchOrderItems = async () => {
    if (!selectedStore || !id) return;
    setLoading(true);
    try {
      const result = await client.getDeliveryOrderItems(selectedStore.storeNumber ?? '0', id);
      if (result.success && result.data) {
        setDeliveryItems(result.data);
        setFilteredItems(result.data);
        setPage(0);

        const quantities: { [key: number]: number } = {};
        const uoms: { [key: number]: string } = {};
        const notes: { [key: number]: string } = {};
        result.data.forEach(item => {
          quantities[item.ItemId] = item.ReceivedQuantity ?? item.ShippedQuantity ?? 0;
          uoms[item.ItemId] = item.ReceivedUom ?? 'CS';
          notes[item.ItemId] = item.Notes ?? '';
        });
        setItemQuantities(quantities);
        setItemUoms(uoms);
        setItemNotes(notes);
        setReceiveEnabled(result.data.every(i => quantities[i.ItemId] >= 0));

        const exceeds = result.data.some(
          it => it.ConvertedUomCreditQuantity != null &&
            it.ShippedQuantity != null &&
            it.ConvertedUomCreditQuantity > it.ShippedQuantity,
        );
        if (exceeds) {
          handleSnackbarOpen(
            t('deliveriesList.convertedExceedsShippedMessage') ||
            'One or more items have a converted quantity greater than the shipped quantity.',
            'warning',
          );
        }
      }
    } catch (error) {
      console.error('Failed to fetch order items:', error);
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderItems();
  }, [selectedStore, id]);

  const handleQuantityChange = (itemId: number, shippedQuantity: number, value: number) => {
    setItemQuantities(prev => ({ ...prev, [itemId]: value }));
    setModifiedItems(prev => new Set(prev).add(itemId));

    const allFilled = deliveryItems.every(item => (itemQuantities[item.ItemId] ?? value) >= 0);
    setReceiveEnabled(allFilled);
  };

  const handleUomChange = (itemId: number, value: string) => {
    setItemUoms(prev => ({ ...prev, [itemId]: value }));
    setModifiedItems(prev => new Set(prev).add(itemId));
  };

  const handleNotesChange = (itemId: number, value: string) => {
    setItemNotes(prev => ({ ...prev, [itemId]: value }));
    setModifiedItems(prev => new Set(prev).add(itemId));
  };

  const handleNotesIconClick = (itemId: number) => {
    setNotesModalItemId(itemId);
    setNotesModalText(itemNotes[itemId] ?? '');
    setNotesModalOpen(true);
  };

  const handleNotesModalClose = () => {
    setNotesModalOpen(false);
    setNotesModalItemId(null);
    setNotesModalText('');
  };

  const handleNotesModalSave = () => {
    if (notesModalItemId !== null) {
      handleNotesChange(notesModalItemId, notesModalText);
    }
    handleNotesModalClose();
  };

  const handleSaveChanges = async () => {
    if (!selectedStore) return;
    setLoading(true);
    try {
      const updates = deliveryItems.map(item => ({
        ItemId: item.ItemId,
        OrderId: parseInt(id ?? '0'),
        ReceivedQuantity: itemQuantities[item.ItemId] ?? item.ShippedQuantity ?? 0,
        ReceivedUom: itemUoms[item.ItemId] ?? 'CS',
        Notes: itemNotes[item.ItemId] ?? '',
      }));
      const result = await client.updateDeliveryItems(selectedStore.storeNumber ?? '0', updates);
      if (result.success) {
        handleSnackbarOpen(t('deliveriesList.updateSuccess'), 'success');
        setModifiedItems(new Set());
        await fetchOrderItems();
      } else {
        handleSnackbarOpen(result.message || t('deliveriesList.updateError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.updateError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReceive = async () => {
    if (!selectedStore || !id) return;
    setLoading(true);
    try {
      const result = await client.markOrderAsReceived(selectedStore.storeNumber ?? '0', id);
      if (result.success) {
        handleSnackbarOpen(t('deliveriesList.receivedSuccess'), 'success');
        setModifiedItems(new Set());
        await fetchOrderItems();
      } else {
        handleSnackbarOpen(result.message || t('deliveriesList.receivedError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.receivedError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const checkDiscrepancies = () => {
    const hasDiscrepancy = deliveryItems.some(
      item => (item.ConvertedUomCreditQuantity != null && item.ConvertedUomCreditQuantity < item.ShippedQuantity) ,
    );
    setDiscrepanciesExist(hasDiscrepancy);
  };

  const handleReviewAndComplete = async () => {
    checkDiscrepancies();
    setReviewModalOpen(true);
  };

  const handleReviewModalClose = () => setReviewModalOpen(false);

  const handleContinueWithoutCreditRequest = async () => {
    setReviewModalOpen(false);
    await handleReceive();
  };

  const handleCreateCreditRequest = async () => {
    if (!selectedStore || !id) return;
    setLoading(true);
    try {
      const result = await client.createCreditRequest(selectedStore.storeNumber ?? '0', id);
      if (result.success) {
        navigate(`/deliveries/${id}/credit`, { state: { successMessage: t('deliveriesList.creditRequestCreateSuccess') } });
        handleReviewModalClose();
      } else {
        handleSnackbarOpen(result.message || t('deliveriesList.creditRequestCreateError'), 'error');
        setReviewModalOpen(false);
      }
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.creditRequestCreateError') + ' ' + error, 'error');
      setReviewModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    handleSaveChanges();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleViewCreditRequest = () => {
    navigate(`/deliveries/${id}/credit`);
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(0);

    if (!query.trim()) {
      setFilteredItems(deliveryItems);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = deliveryItems.filter(item =>
      item.ItemId.toString().toLowerCase().includes(lowerQuery) ||
      item.Name.toLowerCase().includes(lowerQuery) ||
      (item.Barcode && item.Barcode.toLowerCase().includes(lowerQuery)),
    );
    setFilteredItems(filtered);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredItems(deliveryItems);
    setPage(0);
  };

  const formDirty = modifiedItems.size > 0;

  const visibleItems = filteredItems.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredItems.length / pageSize) || 1;

  return (
    <PageArea>
      {userIsReviewer ? (
        <Grid item
          xs={12}
          mb={10}>
          <Box mb={2}>
            <Breadcrumbs aria-label="breadcrumb"
              sx={{ fontSize: 14 }}>
              <Link
                underline="hover"
                color="inherit"
                onClick={() => navigate('/deliveries')}
                sx={{ cursor: 'pointer' }}
              >
                {t('deliveriesList.breadcrumbDeliveries')}
              </Link>
              <Typography color="text.primary">
                {t('deliveriesList.breadcrumbDeliveryDetail')}
              </Typography>
            </Breadcrumbs>
          </Box>
        </Grid>
      ) : (
        <Grid item
          xs={12}
          mb={10}>
          <BackButton handleClick={() => navigate('/deliveries')}
            title={t('deliveriesList.exit')}
            isLink={false} />
        </Grid>
      )}

      <Grid container
        alignItems="center"
        justifyContent="space-between"
        mb={5}>
        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PageTitle>
              {t('deliveriesList.delivery')}: {id}
            </PageTitle>
            {deliveryItems && (
              <OrderStatusChip orderStatus={orderIsReceived ? t('deliveriesList.received') : t('deliveriesList.notReceived')} />
            )}
          </Box>
        </Grid>
        {!orderIsReceived && hasPermissionTo(['DeliveriesSave']) &&
          <Grid item
            sx={{ display: 'flex', gap: 2 }}>
            <Button variant="primary"
              size="lg"
              disabled={loading || !formDirty && !receiveEnabled && !orderIsReceived}
              onClick={handleSaveChanges}
              sx={{ minWidth: '200px' }}>
              {t('deliveriesList.saveProgress')}
            </Button>
            <Tooltip title={formDirty ? t('deliveriesList.saveBeforeReview') : t('deliveriesList.reviewCompleteTooltip')}>
              <span>
                <Button variant="secondary"
                  size="lg"
                  disabled={!receiveEnabled || loading || formDirty}
                  onClick={handleReviewAndComplete}
                  sx={{ minWidth: '200px' }}>
                  {t('deliveriesList.reviewAndComplete')}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        }
        {orderIsReceived && creditRequestExists && hasPermissionTo(['CreditRequestsView']) &&
          <Grid item
            sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title={formDirty ? t('deliveriesList.saveBeforeViewCredit') : ''}>
              <span>
                <Button variant="secondary"
                  size="lg"
                  disabled={loading || formDirty}
                  onClick={handleViewCreditRequest}
                  sx={{ minWidth: '200px' }}>
                  {t('deliveriesList.viewCreditRequest')}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        }
        {orderIsReceived && !userIsReviewer && !creditRequestExists && withinClaimPeriod && hasPermissionTo(['CreditRequestsSave']) &&
          <Grid item
            sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title={formDirty ? t('deliveriesList.orderWithinClaimPeriod') : ''}>
              <span>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={loading || formDirty}
                  onClick={handleCreateCreditRequest}
                  sx={{ minWidth: '200px' }}>
                  {t('deliveriesList.createCreditRequest')}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        }
      </Grid>

      <Grid container>
        <Grid item
          xs={12}
          mb={3}>
          <TextField
            fullWidth
            size="small"
            label={t('deliveriesList.searchByProduct')}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchSm style={{ marginRight: 8 }} />,
              endAdornment: searchQuery && (
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                >
                  <Clear />
                </IconButton>
              ),
            }}
            sx={{ maxWidth: '500px' }}
          />
        </Grid>
        <Grid item
          xs={12}>
          {loading ? <LoadingWheel /> :
            <TableContainer>
              <Table size="small"
                aria-label="delivery table"
                sx={{ minWidth: '700px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>{t('deliveriesList.productId')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>{t('deliveriesList.description')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('deliveriesList.orderedQuantity')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('deliveriesList.shippedQuantity')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('deliveriesList.receivedQuantity')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('deliveriesList.receivedUom')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('deliveriesList.shippedUomEquivalent')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('deliveriesList.notes')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleItems.map(item => (
                    <TableRow key={item.ItemId}>
                      <TableCell><TextWithOverflowTooltip text={item.ItemId.toString()} /></TableCell>
                      <TableCell><TextWithOverflowTooltip text={item.Name} /></TableCell>
                      <TableCell align={'center'}>{`${item.OrderedQuantity ?? 0} ${item.OrderedUom ?? 'CS'}`}</TableCell>
                      <TableCell align={'center'}>{`${item.ShippedQuantity ?? 0} ${item.OrderedUom ?? 'CS'}`}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0 }}
                            disabled={loading || orderIsReceived || !hasPermissionTo(['DeliveriesSave'])}
                            value={itemQuantities[item.ItemId]}
                            onChange={e => handleQuantityChange(item.ItemId, item.ShippedQuantity ?? 0, Number(e.target.value))}
                            sx={{
                              width: '100px',
                              input: { textAlign: 'center' },
                              minWidth: '100px',
                              flexShrink: 0,
                              mr: 1,
                            }}
                          />
                          <Box sx={{ width: 20, display: 'flex', justifyContent: 'center' }}>
                            {(item.CreditRequestItemID !== undefined && item.CreditRequestItemID !== null)
                || (withinClaimPeriod && item.ConvertedUomCreditQuantity != null && item.ConvertedUomCreditQuantity < item.ShippedQuantity  ) ? (
                                <Tooltip title={t('deliveriesList.discrepancyTooltip')}>
                                  <ErrorOutlineIcon
                                    sx={{
                                      fontSize: 18,
                                      color: 'warning.main',
                                      verticalAlign: 'middle',
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Box sx={{ width: 18 }} />
                              )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <TextField
                          select
                          size="small"
                          value={itemUoms[item.ItemId] ?? 'CS'}
                          disabled={loading || orderIsReceived || !hasPermissionTo(['DeliveriesSave'])}
                          onChange={e => handleUomChange(item.ItemId, e.target.value)}
                          sx={{ width: '100px', input: { textAlign: 'center' } }}
                        >
                          {UOM_OPTIONS.map(uom => <MenuItem key={uom.value}
                            value={uom.value}>{uom.label}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell align="center">
                        {item.ConvertedUomCreditQuantity != null ? item.ConvertedUomCreditQuantity : item.ShippedQuantity} {item.OrderedUom}
                        {item.ConvertedUomCreditQuantity != null && item.ShippedQuantity != null && item.ConvertedUomCreditQuantity > item.ShippedQuantity && (
                          <Tooltip title={t('deliveriesList.convertedExceedsShipped') || 'Received quantity exceeds shipped quantity'}>
                            <WarningAmber sx={{ ml: 2, color: 'warning.main', fontSize: 18 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="primary"
                          onClick={() => handleNotesIconClick(item.ItemId)}><Note /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <CustomPagination
                page={page}
                setPage={setPage}
                maxPages={totalPages}
                breakpointForChangeDisplay={120000}
              />
            </TableContainer>
          }
        </Grid>
      </Grid>

      <Dialog open={notesModalOpen}
        onClose={handleNotesModalClose}>
        <DialogTitle>{t('deliveriesList.notes')}</DialogTitle>
        <DialogContent>
          <TextField multiline
            rows={4}
            disabled={loading || orderIsReceived || !hasPermissionTo(['DeliveriesSave'])}
            fullWidth
            value={notesModalText}
            onChange={e => setNotesModalText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNotesModalClose}>{t('deliveriesList.close')}</Button>
          {!orderIsReceived && hasPermissionTo(['DeliveriesSave']) &&
            <Button onClick={handleNotesModalSave}>{t('deliveriesList.confirm')}</Button>
          }
        </DialogActions>
      </Dialog>

      <Dialog open={reviewModalOpen}
        onClose={handleReviewModalClose}>
        <DialogTitle>{t('deliveriesList.reviewAndCompleteDelivery')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deliveriesList.reviewCompleteDialogText')}
          </DialogContentText>
          {discrepanciesExist && withinClaimPeriod && (
            <Box mt={2}
              sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
              <WarningAmber sx={{ mr: 3 }} />
              {t('deliveriesList.discrepancyWarning')}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Button
            onClick={handleReviewModalClose}
            variant="bad"
            sx={{ minWidth: 160 }}
          >
            {t('deliveriesList.cancel')}
          </Button>
          <Button
            onClick={handleContinueWithoutCreditRequest}
            variant={discrepanciesExist && withinClaimPeriod ? 'secondary' : 'primary'}
          >
            {discrepanciesExist && withinClaimPeriod ? t('deliveriesList.continueWithoutCreditRequest') : t('deliveriesList.continue')}
          </Button>
          {discrepanciesExist && withinClaimPeriod && (
            <Button
              onClick={handleCreateCreditRequest}
              variant="primary"
              color="error"
              sx={{ minWidth: 180 }}
            >
              {t('deliveriesList.createCreditRequest')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <MessagesSnackbar open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={3000} />
      <LeavePageModal open={blocker.state === 'blocked' && modifiedItems.size > 0}
        onConfirmUrl='/deliveries'
        onConfirm={handleLeavePageModalConfirm}
        onClose={handleLeavePageModalClose}
        onCancel={handleLeavePageModalCancel} />
    </PageArea>
  );
};

export default DeliveryDetailsOfd;
